import { TickLabelProps } from '@visx/axis'
import { NumberLike } from '@visx/scale'
import moment from 'moment'
import { BAR_COLORS, SINGLE_BAR_COLOR, STYLE_PRESETS } from 'apps/tms/constants/charts'
import { FORMATS } from 'constants/datetime'
import { TCurrencyPieChartDatum, IDatumType } from '../types'

/**
 * Constant representing placeholder data that will force the donut to render
 * as a single section, used when rendering an "empty" state.
 */
export const NO_DATA: TCurrencyPieChartDatum[] = [{ label: 'no data', value: 1 }]

/**
 * Returns an index and number of colors, so if more than 11 lines are being
 * rendered, it will repeat a color that has already been used.
 */
export const barColorIdx = (idx: number): number => idx % BAR_COLORS.length

export const getFillColor = (i: number, singleColor?: boolean): string => {
  if (singleColor) {
    return SINGLE_BAR_COLOR
  }
  return BAR_COLORS[barColorIdx(i)]
}

/**
 * When showing the x axis ticks, we only care about the first, mid and last values.
 */
export function getBottomAxisTickValues<Data>(data: Data[]): Data[] {
  const lastItemIndex = data.length - 1
  const low = data[0]
  const mid = data[Math.round(lastItemIndex / 2)]
  const high = data[lastItemIndex]

  return [low, mid, high]
}

export const numberLikeToString = (value: NumberLike): string => `${value}`

export const formatHoursAndMinutes = (time: NumberLike): string => {
  const hours = parseInt(time.toString(), 10)
  const minutes = (time.valueOf() - Math.floor(time.valueOf())) * 60
  const hoursToRender = hours > 0 ? `${hours} hrs ` : ''
  const minutesToRender = minutes >= 0 ? `${parseInt(`${minutes}`, 10)} min` : ''

  return `${hoursToRender}${minutesToRender}`
}

type TDateFormatter = (date: string) => string

const monthDayFormatter: TDateFormatter = date => moment(date).utc().format(FORMATS.monthDay)
const weekFormatter: TDateFormatter = date => `Week of ${monthDayFormatter(date)}`
const monthFormatter: TDateFormatter = date => moment(date).utc().format('MMM')

const formattersByTimeRange: Record<string, TDateFormatter> = {
  'Year to Date': monthFormatter,
  'Past Year': monthFormatter,
  'Past 3 Months': weekFormatter,
  'Past 6 Months': weekFormatter,
}

export const getTimeRangeFormatter = (timeRange: string): TDateFormatter =>
  formattersByTimeRange[timeRange] ?? monthDayFormatter

/**
 * Default Accessors
 */
export const getXValue = (d: IDatumType) => d.x ?? ''
export const getYValue = (d: IDatumType) => Number(d.y ?? 0)

export const getLeftAxisTickLabelProps: TickLabelProps<any> = () => ({
  fill: STYLE_PRESETS.axisColor,
  fontSize: STYLE_PRESETS.axisLabelFontSize,
  lineHeight: STYLE_PRESETS.axisLabelLineHeight,
  dy: -STYLE_PRESETS.axisLabelYOffset,
  textAnchor: 'start',
  style: {
    fontWeight: STYLE_PRESETS.axisLabelFontWeight,
  },
})

export const getBottomAxisTickLabelProps: TickLabelProps<any> = () => ({
  fill: STYLE_PRESETS.axisColor,
  fontSize: STYLE_PRESETS.axisLabelFontSize,
  lineHeight: STYLE_PRESETS.axisLabelLineHeight,
  dy: -STYLE_PRESETS.axisLabelYOffset,
  textAnchor: 'middle',
  verticalAnchor: 'end',
  style: {
    fontWeight: STYLE_PRESETS.axisLabelFontWeight,
  },
})

/**
 * silly type casting 🤷
 */
export const processChartData = (data: any[] = []): IDatumType[][] => {
  return data.map(({ dataPoints }) => dataPoints)
}

const currencyFormatter = new Intl.NumberFormat('en', {
  style: 'currency',
  currency: 'USD',
})

export const formatCurrency = (value: NumberLike = 0) => currencyFormatter.format(value.valueOf())

const compactCurrencyFormatter = new Intl.NumberFormat('en', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
})

export const formatCompactCurrency = (value: NumberLike = 0) =>
  compactCurrencyFormatter.format(value.valueOf())

const makeDecimalFormatter = (decimalPlaces = 0) => (value: NumberLike = 0) => {
  const number = value.valueOf()

  return `${number.toFixed(decimalPlaces)}`
}

export const formatTwoDecimalNumber = makeDecimalFormatter(2)

export const formatInteger = makeDecimalFormatter()

export const formatCurrencyWithPercentage = (value: number, total: number) => {
  // We check if total is 0 to avoid dividing by it; otherwise we calculate its respective percentage.
  const percentageOfTotal = total > 0 ? Math.trunc((value / total) * 100) : 0

  return `${formatCurrency(value)} (${percentageOfTotal}%)`
}
